import React from 'react';
import ChangeIndicator from './ChangeIndicator';
import { KPI_ACCENT_BORDERS, KPI_ICON_COLORS, KPI_ICONS } from '../shared/KpiCard';

// A subtle background tint per accent, so each tile reads as a coloured card rather
// than a white box with a coloured top rule. Decorative only, like the accent itself.
const KPI_BG_TINTS = {
  primary: 'bg-[#32B1FF]/[0.06] dark:bg-[#32B1FF]/10',
  success: 'bg-teal-50/70 dark:bg-teal-500/10',
  muted: 'bg-sky-50/70 dark:bg-sky-500/10',
  subtle: 'bg-indigo-50/70 dark:bg-indigo-500/10',
  highlight: 'bg-fuchsia-50/70 dark:bg-fuchsia-500/10',
};

/**
 * One headline stat: label, big value, optional change indicator, optional sub-label.
 *
 * ── The house KPI anatomy, not a plain box (spec v2 §4) ─────────────────────────
 * This used to be a flat bordered rectangle with a label and a number, which read as
 * placeholder next to the KPI cards on every other dashboard — especially on a property
 * with no year-over-year comparison, where the change indicator is correctly absent and
 * the tile is left holding two lines of text.
 *
 * It now uses `KpiCard`'s own anatomy and its own tokens: a coloured top rule, an
 * oversized watermark icon bled off the top-right corner, and the house label and value
 * type scale. §4 settles the tie — "consistency across the dashboard wins over this page
 * having its own look."
 *
 * ── Why not `KpiCard` itself ────────────────────────────────────────────────────
 * `KpiCard` hardcodes `DeltaBadge`, which renders an em dash when the change is null.
 * §8 forbids exactly that: when `comparison_available` is false there is no comparison,
 * and a dash reads as "flat" rather than as "not measured". `ChangeIndicator` exists for
 * that reason and renders NOTHING for a null. So the tile borrows the anatomy and the
 * palette while keeping its own change treatment.
 *
 * ── Why the accent is decorative and never semantic ─────────────────────────────
 * `variant` picks one of `KpiCard`'s five accent colours, which distinguish the tiles
 * from one another and mean nothing on their own. It is deliberately NOT the
 * emerald/amber/red scale `MetricCardShell` uses: those encode good/warning/bad, and §8
 * keeps that judgement off this page while §12 bans alert styling outright. A green
 * "Position" tile would be the page deciding 10.3 is good.
 *
 * `value` and `subLabel` arrive PRE-FORMATTED — the tile never converts, rounds or
 * derives anything (§8). A falsy `value` renders the tile without a number rather than
 * printing `undefined`.
 *
 * @param {string} label
 * @param {string} value pre-formatted display string
 * @param {number|null} [changePct] already-percent change; absent → no indicator (§8)
 * @param {'auto'|'neutral'} [changeTone]
 * @param {React.ReactNode} [description] one plain-language line explaining what the
 *        metric counts — sits under the number; distinct from `subLabel`, which is the
 *        year-over-year direction pinned to the bottom of the tile
 * @param {React.ReactNode} [subLabel] small line under the value
 * @param {'primary'|'success'|'muted'|'subtle'|'highlight'} [variant] decorative accent only
 * @param {string} [icon] a key of `KPI_ICONS`
 * @param {Function} [onClick] when set, the whole tile becomes a button (used by the
 *        device split, whose figures drill); absent → a static div. Extra props (e.g.
 *        `data-device`) are spread onto the root element.
 */
const MetricTile = ({
  label,
  value,
  changePct,
  changeTone = 'auto',
  description,
  subLabel,
  variant = 'primary',
  icon = 'chart',
  onClick,
  ...rest
}) => {
  const className = `relative ${KPI_BG_TINTS[variant] ?? KPI_BG_TINTS.primary} rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 border-t-[3px] ${
    KPI_ACCENT_BORDERS[variant] ?? KPI_ACCENT_BORDERS.primary
  } p-4 min-h-[112px] flex flex-col overflow-hidden text-left${
    onClick
      ? ' w-full cursor-pointer transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]'
      : ''
  }`;

  const body = (
    <>
      <div className="absolute inset-0 overflow-hidden rounded-xl pointer-events-none">
        <div className={`absolute -top-4 -right-4 ${KPI_ICON_COLORS[variant] ?? KPI_ICON_COLORS.primary}`}>
          <svg className="w-20 h-20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" aria-hidden="true">
            {KPI_ICONS[icon] ?? KPI_ICONS.chart}
          </svg>
        </div>
      </div>

      <p className="relative z-10 text-[12px] sm:text-[13px] uppercase tracking-wide font-semibold text-gray-500 dark:text-gray-400">
        {label}
      </p>
      {value ? (
        <p className="relative z-10 mt-1.5 text-2xl lg:text-xl xl:text-2xl font-extrabold text-gray-900 dark:text-gray-100 tabular-nums whitespace-nowrap">
          {value}
        </p>
      ) : null}
      {/* `ChangeIndicator` renders null for a null change (§8 — no dash, no zero), so the
          wrapper is conditional too: an empty div would still take its margin and leave a
          gap under the number on every property with no comparison. */}
      {changePct != null && (
        <div className="relative z-10 mt-1">
          <ChangeIndicator value={changePct} tone={changeTone} />
        </div>
      )}
      {description ? (
        <p className="relative z-10 mt-1.5 text-xs text-gray-500 dark:text-gray-400 leading-snug">
          {description}
        </p>
      ) : null}
      {subLabel ? (
        <div className="relative z-10 mt-auto pt-1.5 text-sm text-gray-500 dark:text-gray-400 leading-snug">
          {subLabel}
        </div>
      ) : null}
    </>
  );

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={className} {...rest}>
        {body}
      </button>
    );
  }
  return (
    <div className={className} {...rest}>
      {body}
    </div>
  );
};

export default MetricTile;
